import get from 'lodash.get';
import { selectors } from '@yola/subscription-manager-js';
import { errors, readOnly } from '@yola/ws-sdk';
import store from 'src/js/store';
import utils from 'yola-editor/src/js/router/utils';
import errorModule from 'src/js/modules/error';
import segment from 'src/js/modules/analytics/segment';
import config from 'src/js/modules/config';
import user from 'yola-editor/src/js/modules/user';
import site from 'yola-editor/src/js/modules/site';
import i18n from 'yola-editor/src/js/modules/i18n';
import getUpsellSettings from 'yola-editor/src/js/modules/upsell/helpers/get-upsell-settings';
import getPaymentURL from 'yola-editor/src/js/modules/upsell/selectors/payment-url';
import startB2CDomainPurchase from 'yola-editor/src/js/modules/upsell/actions/start-b2c-domain-purchase';
import startWLDomainPurchase from 'yola-editor/src/js/modules/upsell/actions/start-wl-domain-purchase';
import beforeUnloadListener from 'yola-editor/src/js/utils/before-unload-listener';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import featureFlagsModule from 'yola-editor/src/js/modules/feature-flags/service-client';
import isFreeDomainAvailable from 'yola-editor/src/js/modules/upsell/helpers/is-free-domain-available';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import publishingConstants from 'yola-editor/src/js/modules/publishing/constants';
import getHelpMenuItemClickCallbacks from './get-help-menu-item-click-callbacks';
import getRequestBlock from '../helpers/get-request-block';
import getRequestWidget from '../helpers/get-request-widget';
import dialogs from '../../dialogs';
import getHelpMenuItems from './get-help-menu-items';
import { helpMenuUrls } from '../constants';
import onSwitchTemplateTrack from '../helpers/on-switch-template';

const { YOLA_PARTNER_PUBLISHING_TYPE } = publishingConstants;
const {
  constants: { triggerIds },
} = segment;

const getEditorSettings = async () => {
  const state = store.getState();
  const featureFlagsManager = featureFlagsModule.get();

  const paymentURL = getPaymentURL(state);
  const activeSubscriptions = selectors.getActiveSubscriptions(state);
  const allSubscriptionsList = selectors.getSubscriptionList(state);
  const onlineStoreUrl = config.selectors.getOnlineStoreUrl(state);
  const myyolaUrl = config.selectors.getMyyolaUrl(state);
  const partnerDashboardUrl = user.selectors.getPartnerDashboardUrl(state);
  const userPreferences = user.selectors.getUserPreferences(state);
  const userData = user.selectors.getUserData(state);
  const availablePlatformComponents = user.selectors.getAvailablePlatformComponents(state);
  const isTemplateSwitched = site.selectors.getTemplateSwitched(state);
  const locale = i18n.selectors.getLocale(state);
  const publishedSiteData = site.selectors.getPublishedSiteData(state);
  const publicApiUrl = config.selectors.getApiUrl(state);
  const partnerData = user.selectors.getPartnerData(state);
  const externalUpsellTypes = user.selectors.getExternalUpsellTypes(state);
  const partnerName = partnerData.name;
  const partnerId = partnerData.id;
  const { siteHeader, siteFooter, ...wsBranding } = partnerData.properties.wsBranding;
  const publishingType = user.selectors.getPublishingType(state);
  const supportUrl = user.selectors.getSupportUrl(state);
  const supportEmail = user.selectors.getSupportEmail(state);
  const contactSupportUrl = user.selectors.getContactSupportUrl(state);
  const termsOfUseUrl = user.selectors.getTermsOfUseUrl(state);
  const privacyPolicyUrl = user.selectors.getPrivacyPolicyUrl(state);
  const isAvailableRoadmapFromPartner = get(partnerData, 'properties.wsFeatures.publicRoadmap');
  const isWL = user.selectors.getWhiteLabelStatus(state);
  const hasSiteToUnpublish =
    publishedSiteData && (!isWL || publishingType === YOLA_PARTNER_PUBLISHING_TYPE);

  // we should featch feature flags before limits calculation, because of workaround
  // with feature flags in getFeatureLimits function. getFeatureLimits is synchronous
  // function, so we can't load flags dynamically. We use featureFlagsManager state
  // in getFeatureLimits to get loaded feature flag and calculate limits properly.
  const {
    branded_header: isBrandedHeaderEnabled,
    freedomain: isFreeDomainEnabled,
    difm_latitude: isDifmLatitudeEnabled,
    loopia_trial_publish_upgrade: isLoopiaTrialPublishUpgradeEnabled,
  } = await featureFlagsManager.request([
    'branded_header',
    'freedomain',
    'difm_latitude',
    'legacy_latitude_templates_eol',
    'remove_partner_ads',
    'loopia_trial_publish_upgrade',
  ]);

  const limits = getCurrentPackageFeatureLimits();
  const htaccessSupported = user.selectors.getHtaccessSupported(state);
  const isCleanUrlsSupported =
    publishingType === YOLA_PARTNER_PUBLISHING_TYPE || Boolean(htaccessSupported);
  const { available: isOnlineStoreAvailable } = limits.onlineStore;
  const { id: siteId } = site.selectors.getSiteData(state);
  const { SESSION_EXPIRED } = errorModule.constants;
  const { SITE_ID_TO_SWITCH, TEMPLATE_BUILD_SLUG } = site.constants;

  const galleryLink = config.selectors.getGalleryLink(state);
  const templateBuildSlug = site.selectors.getConceptOrBuildSlug(state);
  const galleryRedirectUrl = `${galleryLink}?${SITE_ID_TO_SWITCH}=${siteId}&${TEMPLATE_BUILD_SLUG}=${templateBuildSlug}`;

  const hasAvailableFreeDomain = isFreeDomainAvailable(
    activeSubscriptions,
    allSubscriptionsList,
    isFreeDomainEnabled
  );
  const hasTrialPackage = Boolean(selectors.getCurrentPackageTrialStatus(state));
  const upsells = getUpsellSettings({
    state,
    dispatch: store.dispatch,
    isFreeDomainAvailable: hasAvailableFreeDomain,
    isLoopiaTrialPublishUpgrade: isLoopiaTrialPublishUpgradeEnabled && isWL && hasTrialPackage,
  });

  const isDialogVisible = dialogs.verifiers.isVisible();
  const featureFlags = featureFlagsManager.state;

  let redirectToOnlineStore;
  let redirectToDashboard;
  let publicRoadmapUrl;
  let requestBlock;
  let requestWidget;

  if (isOnlineStoreAvailable) {
    redirectToOnlineStore = () => {
      utils.redirectToUrl(onlineStoreUrl);
    };
  }

  if (availablePlatformComponents.includes(user.platformComponents.DASHBOARD)) {
    redirectToDashboard = () => {
      utils.redirectToUrl(myyolaUrl);
    };
  }

  if (isWL && partnerDashboardUrl) {
    redirectToDashboard = () => {
      utils.redirectToUrl(partnerDashboardUrl, { topWindow: true });
    };
  }

  if (isAvailableRoadmapFromPartner) {
    publicRoadmapUrl = config.selectors.getPublicRoadmapURL(state);
    requestBlock = getRequestBlock(publicRoadmapUrl);
    requestWidget = getRequestWidget(publicRoadmapUrl);
  }

  // In sdk we set default welcomeTourCompleted to 'True' if it doesnt exist.
  // This is done in order to make editor work correctly without yola-editor.
  // But it makes welcome tour to be never shown. So in order to make welcome tour shown when ws-editor
  // is in pair with yola-editor we set "default" falsy value for `welcomeTourCompleted` here

  if (userPreferences && !userPreferences.welcomeTourCompleted) {
    userPreferences.welcomeTourCompleted = false;
  }

  const isComplianceServiceMenuItemEnabled = externalUpsellTypes.includes(
    user.constants.externalUpsellTypes.DIFM_PRIVACY
  );
  const isOrderWebsiteMenuItemEnabled =
    isDifmLatitudeEnabled &&
    (!isWL || externalUpsellTypes.includes(user.constants.externalUpsellTypes.DIFM_SERVICE));
  const helpMenuItemClickCallbacks = getHelpMenuItemClickCallbacks(triggerIds.EDITOR_HEADER);

  const settings = {
    limits,
    ui: {
      isDialogVisible,
    },
    helpMenu: getHelpMenuItems({
      isWL,
      partnerId,
      contactSupportUrl,
      supportEmail,
      gettingStartedUrl: helpMenuUrls.GETTING_STARTED,
      helpCenterUrl: supportUrl,
      publicRoadmapUrl,
      termsOfUseUrl,
      privacyPolicyUrl,
      isComplianceServiceMenuItemEnabled,
      isOrderWebsiteMenuItemEnabled,
      helpMenuItemClickCallbacks,
    }),
    featureFlags,
    userData,
    userPreferences,
    branding: {
      ...wsBranding,
      siteHeader: isBrandedHeaderEnabled ? siteHeader : null,
      siteFooter: isBrandedHeaderEnabled ? null : siteFooter,
    },
    partnerData: {
      name: partnerName,
    },
    upsells,
    locale,
    requestBlock,
    requestWidget,
    onCriticalError({ errorMessage, errorInfo, networkErrorDetails }) {
      const error = errorModule.helpers.createEditorError({ errorMessage, networkErrorDetails });
      errors.operations.catchError(error, errors.constants.errorTypes.CRITICAL_ERROR, errorInfo);
    },
    onResetSite() {
      store.dispatch(dialogs.actions.show(dialogs.dialogTypes.SITE_RESET));
    },
    onReloadEditor() {
      utils.reload();
    },
    onSessionExpired() {
      errors.operations.catchError(new Error(SESSION_EXPIRED));
    },
    onUpdateSiteName({ siteName }) {
      store.dispatch(
        site.actions.updateSiteData({
          name: siteName,
        })
      );
    },
    updateUserPreference({ properties }) {
      const { property, value } = properties;
      store.dispatch(user.actions.updateUserPreference(property, value));
    },
    onDomainSelector({ triggerId }) {
      store.dispatch(startB2CDomainPurchase(triggerId));
    },
    onWLDomainSelector({ triggerId }) {
      store.dispatch(startWLDomainPurchase(triggerId));
    },
    onSwitchTemplate({ sendAnalytics, traits } = {}) {
      const isReadOnly = readOnly.verifiers.isReadOnlyModeEnabled();
      if (isReadOnly) {
        errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
        return;
      }
      if (sendAnalytics) {
        onSwitchTemplateTrack({ templateBuildSlug, siteId, ...traits }).then(() => {
          redirectToUrl(galleryRedirectUrl);
        });
      } else {
        redirectToUrl(galleryRedirectUrl);
      }
    },
    beforeUnloadEvents: {
      start() {
        beforeUnloadListener.add();
      },
      end() {
        beforeUnloadListener.remove();
      },
    },
    requestFeatureFlags: async ({ flags }) => {
      const resolvedFlags = await featureFlagsManager.request(flags);
      return {
        flags: resolvedFlags,
      };
    },
    getFeatureFlagsState() {
      return featureFlagsManager.state;
    },
    redirectToDashboard,
    isTemplateSwitched,
    publicApiUrl,
    paymentURL,
    isCleanUrlsSupported,
  };

  if (isOnlineStoreAvailable) {
    settings.onlinestore = {
      redirect: redirectToOnlineStore,
    };
  }

  if (hasSiteToUnpublish) {
    settings.onUnpublish = () => {
      store.dispatch(dialogs.actions.show(dialogs.dialogTypes.UNPUBLISH_CONFIRM_ACTION));
    };
  } else {
    settings.onUnpublish = null;
  }

  return settings;
};

export default getEditorSettings;
